import { Box, Stack, Typography, useTheme, alpha } from "@mui/material";
import type { RoadmapWeek } from "../../api/client";
import StatusDot from "./StatusDot";
import TaskCard from "./TaskCard";

interface WeekRailProps {
  week: RoadmapWeek;
  isFirstInPhase?: boolean;
  compact?: boolean;
  focusTaskId?: string;
  onToggle: (taskId: string, next: boolean) => void;
}

const DOT_SIZE = 28;
const DOT_COL_WIDTH = 40; // dot + breathing room for the connector rail
const DOT_CENTER_X = DOT_SIZE / 2; // 14px

// Spec §6.4 — week head: dot · title · meta. Task stack indented under the
// dot. Vertical 1.5px connector rail between consecutive weeks (hides on
// mobile per spec §9). First week in a phase has no connector above it.
//
// `compact` mode renders header only — used for phases 3+4 per spec §5.2,
// which deliberately hides far-future task detail to avoid overwhelming a
// week-3 user.
export default function WeekRail({
  week,
  isFirstInPhase = false,
  compact = false,
  focusTaskId,
  onToggle,
}: WeekRailProps) {
  const theme = useTheme();
  const tasks = week.tasks ?? [];
  const doneCount = tasks.filter((t) => t.done).length;
  const ratio = tasks.length > 0 ? `${doneCount}/${tasks.length}` : null;

  return (
    <Box
      component="article"
      id={`week-${week.index}`}
      data-week-index={week.index}
      sx={{
        position: "relative",
        scrollMarginTop: "88px", // sticky header (72px) + 16px breathing
        // Connector rail above the dot, drawn for every week except the first.
        ...(!isFirstInPhase && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: `${DOT_CENTER_X - 0.75}px`,
            top: "-24px",
            height: "24px",
            width: "1.5px",
            bgcolor: alpha(theme.palette.text.primary, 0.12),
            [theme.breakpoints.down("md")]: {
              display: "none",
            },
          },
        }),
      }}
    >
      {/* Week head: dot · title · meta */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${DOT_COL_WIDTH}px 1fr auto`,
          alignItems: "center",
          columnGap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", height: DOT_SIZE }}>
          <StatusDot status={week.status} size="md" />
        </Box>
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{ fontWeight: 500, lineHeight: 1.3 }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              fontWeight: 500,
              color: "text.secondary",
              mr: 1,
              letterSpacing: "0.04em",
            }}
          >
            W{week.index}
          </Box>
          {week.title}
        </Typography>
        {ratio && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", fontWeight: 500 }}
          >
            {ratio}
          </Typography>
        )}
      </Box>

      {/* Task stack — indented under the dot. Hidden in compact mode. */}
      {!compact && tasks.length > 0 && (
        <Stack
          spacing={1.5}
          sx={{
            mt: 1.5,
            pl: { xs: 0, sm: `${DOT_COL_WIDTH}px` },
          }}
        >
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onToggle={onToggle}
              isFocus={task.id === focusTaskId}
              weekIndex={week.index}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
